import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@w7/ui'

/**
 * Book Management domain.
 *
 * One workspace — Net Exposure, Bet Ticker, Bet Ticker Previous (Postgres ≤7
 * days ending today, else BigQuery tickerBQ; Previous New URL redirects here).
 * Company Master is denied at the door; Betfair / flag-final / master ticker
 * screens are not part of this product.
 */

export type BookWorkspaceId = 'book-management' | 'premium-bm'

export type WorkspaceMode =
  | 'net-exposure'
  | 'bet-ticker'
  | 'previous'
  | 'previous-new'

/** Which API serves a mode. */
export type BackendKind = 'live-books' | 'live-ticker'

/** Demo sessions run on fixtures; anything else reads the real services. */
export type DataSourceKind = 'live' | 'mock'

/** Product a bet came from — maps to `/v1/book/ticker` `source`. */
export type BetSource = 'sportsbook' | 'premium-bm'

/** Net Exposure product filter (artemis = Premium BM). */
export type BooksProductScope = 'sportsbook' | 'premium-bm'

export type BetSide = 'BACK' | 'LAY'

/** `statusScope` on `/v1/book/ticker`: open bets vs settled history. */
export type TickerStatusScope = 'current' | 'previous'

/** Open statuses — mirrors `OPEN_BOOK_STATUSES` in bet-service. */
export const CURRENT_TICKER_STATUSES = [
  'EXECUTABLE',
  'EXECUTION_COMPLETE',
  'PENDING',
  'ACCEPTED',
  'MATCHED',
  'PARTIALLY_MATCHED',
  'UNMATCHED',
] as const

/** Settled statuses — mirrors `PREVIOUS_TICKER_STATUSES` in bet-service. */
export const PREVIOUS_TICKER_STATUSES = [
  'WINNER_DECLARED',
  'WINNER_DECLARED_CANCELLED',
  'WINNER_DECLARED_EXECUTABLE',
  'REVERSED_WINNER',
  'CANCELLED',
  /** Result-API settle-match-odds (incl. Betfair Line Session). */
  'SETTLED',
  /** Lab VAR void / Result-API invalid — show on Bet Ticker Previous. */
  'VOID_VAR',
  'INVALID_BET',
  'RT_INVALID_BET',
] as const

export type TickerBetStatus =
  | (typeof CURRENT_TICKER_STATUSES)[number]
  | (typeof PREVIOUS_TICKER_STATUSES)[number]

/** Short labels for the status filter — raw status still shows in the table. */
export const TICKER_STATUS_LABEL: Record<string, string> = {
  EXECUTABLE: 'Executable',
  EXECUTION_COMPLETE: 'Complete',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  MATCHED: 'Matched',
  PARTIALLY_MATCHED: 'Partial',
  UNMATCHED: 'Unmatched',
  WINNER_DECLARED: 'Winner',
  WINNER_DECLARED_CANCELLED: 'Winner cancelled',
  WINNER_DECLARED_EXECUTABLE: 'Winner executable',
  REVERSED_WINNER: 'Reversed',
  CANCELLED: 'Cancelled',
  SETTLED: 'Settled',
  VOID_VAR: 'Void VAR',
  INVALID_BET: 'Invalid bet',
  RT_INVALID_BET: 'RT invalid bet',
}

export function tickerStatusesForScope(scope: TickerStatusScope): readonly string[] {
  return scope === 'previous' ? PREVIOUS_TICKER_STATUSES : CURRENT_TICKER_STATUSES
}

export function tickerStatusLabel(status: string): string {
  return TICKER_STATUS_LABEL[status] ?? status
}

/**
 * One ticker row.
 *
 * Mirrors `BookTickerRow` in bet-service. Deliberately absent: flag, comment
 * and category — the console this replaces had them, `orders.orders` does not,
 * and a column the storage cannot fill is a column that lies.
 */
export interface TickerBet {
  id: string
  placedAt: string | null
  matchedAt: string | null
  memberId: string
  memberUsername: string
  source: BetSource
  sport: string
  eventName: string
  marketId: string
  marketName: string
  selection: string
  side: BetSide
  currency: string
  /** Legacy single odds — LS = Odds Placed (runs); else matched. */
  odds: number
  /** Lab Odds Placed. */
  oddsPlaced: number
  /** Lab Odds Matched. */
  oddsMatched: number
  matchedAmount: number
  unmatchedAmount: number
  /** Lab getAllBets `multiplier` (default 1). */
  multiplier: number
  /** Provenance only — never enriched or stored beyond the row. */
  clientIp: string | null
  /** Lab getAllBets `doubtfull` — yellow row text (text-warnings #f4dc11). */
  doubtfull: boolean
  /** Lab getAllBets `watchlist` — red row text (text-danger). */
  watchlist: boolean
  /** Lab `isFlagged` — required with isBetfairFlagged for reason circle. */
  isFlagged: boolean
  /** Lab `reason` — IncorrectPrice / Sniping / LowLiquidity. */
  reason: string
  /** Lab `isBetfairFlagged` — vendor flag; drives reason circle + row tint. */
  isBetfairFlagged: boolean
}

export interface TickerPage {
  rows: TickerBet[]
  total: number
  page: number
  pageSize: number
  asOf: string
}

export interface TickerFilterOptions {
  sports: string[]
  events: string[]
  markets: string[]
}

/** Lab getAllBets Market Types dropdown (category filter, not market name). */
export const TICKER_MARKET_TYPES = [
  'Match Odds',
  'Bookmaker',
  'Fancy',
  'Linesession',
] as const

export type TickerMarketType = (typeof TICKER_MARKET_TYPES)[number]

export interface TickerFilters {
  members: string[]
  search: string
  sport: string
  event: string
  market: string
  /** Lab `marketType` category, or `all`. */
  marketType: TickerMarketType | 'all'
  side: BetSide | 'all'
  /** Exact `orders.orders.status`, or `all`. */
  status: string
  currency: string
  /** ISO day. Required by the previous modes, optional on the live ones. */
  from: string
  to: string
  stakeMin: number | null
  stakeMax: number | null
  page: number
  pageSize: number
}

/** Server caps a page at 100 rows. */
export const TICKER_PAGE_SIZES = PAGE_SIZE_OPTIONS
export type TickerPageSize = (typeof TICKER_PAGE_SIZES)[number]

/** Settled history is a range query; the API refuses anything wider. */
export const PREVIOUS_RANGE_MAX_DAYS = 31

/**
 * Lab Betfair Bet Ticker BQ switch: `getDiffDays() > 7 || !isSameDay(toDate)`.
 * Within 7 calendar days ending today → Postgres `ticker`; otherwise BigQuery `tickerBQ`.
 */
export const TICKER_BQ_SPAN_DAYS = 7

export const DEFAULT_TICKER_FILTERS: TickerFilters = {
  members: [],
  search: '',
  sport: 'all',
  event: '',
  market: '',
  marketType: 'all',
  side: 'all',
  status: 'all',
  currency: 'all',
  from: '',
  to: '',
  stakeMin: null,
  stakeMax: null,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
}

export const SOURCE_LABEL: Record<BetSource, string> = {
  sportsbook: 'Sportsbook',
  'premium-bm': 'Premium BM',
}

export const WORKSPACE_SOURCE: Record<BookWorkspaceId, BetSource> = {
  'book-management': 'sportsbook',
  'premium-bm': 'premium-bm',
}

export const WORKSPACE_BOOKS_SCOPE: Record<BookWorkspaceId, BooksProductScope> = {
  'book-management': 'sportsbook',
  'premium-bm': 'premium-bm',
}
